/**
 * KnowledgeCenterPage · v2.7.65+E118
 * ══════════════════════════════════════════════════════════════════
 * Admin-console Wiki + PDF manual compile utility.
 *
 * Left rail  · categorised article list (RBAC-filtered by the backend)
 * Right pane · article renderer (markdown → styled HTML)
 * Toolbar    · "Compile User Manual" for every admin, plus a
 *              "Master Manual (Developer)" button that only appears for
 *              derick@bullertaxi.com (`can_master` flag on the list
 *              endpoint response).
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, BookOpen, FileText, Download, ShieldCheck, RefreshCw, Search, ChevronRight } from "lucide-react";
import { useParams } from "react-router-dom";

import adminApi from "@/lib/adminApi";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

/** Minimal markdown renderer — covers headings / bold / italic / code /
 *  bullets / tables. Deliberately dependency-free so the page stays fast. */
function MarkdownBlock({ md }) {
  const html = useMemo(() => renderMarkdown(md || ""), [md]);
  return (
    <div
      className="prose prose-sm dark:prose-invert max-w-none prose-headings:text-amber-500 prose-a:text-sky-500 prose-code:bg-slate-100 dark:prose-code:bg-zinc-900 prose-code:px-1 prose-code:py-0.5 prose-code:rounded prose-table:border prose-th:bg-amber-100 dark:prose-th:bg-amber-900/30 prose-th:px-2 prose-th:py-1 prose-td:border prose-td:px-2 prose-td:py-1"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
}

function renderMarkdown(src) {
  let s = src.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Code fences
  s = s.replace(/```([\s\S]*?)```/g, (_, code) =>
    `<pre class="bg-slate-100 dark:bg-zinc-950 border border-slate-200 dark:border-zinc-800 rounded p-3 overflow-x-auto text-xs"><code>${code.replace(/`/g, "\\`")}</code></pre>`);
  // Headings
  s = s.replace(/^### (.+)$/gm, "<h3>$1</h3>");
  s = s.replace(/^## (.+)$/gm, "<h2>$1</h2>");
  s = s.replace(/^# (.+)$/gm, "<h1>$1</h1>");
  // Bold + italic + inline code
  s = s.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  s = s.replace(/(?<!\*)\*([^*\n]+)\*(?!\*)/g, "<em>$1</em>");
  s = s.replace(/`([^`]+)`/g, "<code>$1</code>");
  // Tables (pipes)
  s = s.replace(/((?:^\|.*\|\n?)+)/gm, (block) => {
    const rows = block.trim().split("\n").map((r) => r.trim());
    if (rows.length < 2) return block;
    const cells = rows.map((r) => r.slice(1, -1).split("|").map((c) => c.trim()));
    if (!/^:?-+:?$/.test(cells[1][0] || "")) return block;
    const header = "<tr>" + cells[0].map((c) => `<th>${c}</th>`).join("") + "</tr>";
    const body = cells.slice(2).map((r) =>
      "<tr>" + r.map((c) => `<td>${c}</td>`).join("") + "</tr>").join("");
    return `<table class="border-collapse w-full my-3"><thead>${header}</thead><tbody>${body}</tbody></table>`;
  });
  // Bullet lists
  s = s.replace(/((?:^[-*] .+\n?)+)/gm, (block) => {
    const items = block.trim().split("\n").map((l) =>
      `<li>${l.replace(/^[-*] /, "")}</li>`).join("");
    return `<ul class="list-disc pl-5 my-2 space-y-1">${items}</ul>`;
  });
  // Paragraphs (blank-line separated)
  s = s.split(/\n{2,}/).map((chunk) => {
    if (/^<(h[1-6]|ul|table|pre)/.test(chunk.trim())) return chunk;
    return `<p>${chunk.replace(/\n/g, "<br/>")}</p>`;
  }).join("\n");
  return s;
}

export default function KnowledgeCenterPage() {
  // v2.7.65+E133 · Deep-link support — /knowledge-center/articles/:slug
  // opens the KC page pre-scrolled to a specific article.
  const { slug: routeSlug } = useParams();

  const [loading, setLoading] = useState(true);
  const [articles, setArticles] = useState([]);
  const [canEdit, setCanEdit] = useState(false);
  const [canMaster, setCanMaster] = useState(false);
  const [tier, setTier] = useState(1);
  const [activeSlug, setActiveSlug] = useState(routeSlug || null);
  const [activeArticle, setActiveArticle] = useState(null);
  const [q, setQ] = useState("");
  const [searching, setSearching] = useState(false);
  const [searchResults, setSearchResults] = useState(null); // null → no search active
  const [compiling, setCompiling] = useState(null); // "user" | "master" | null

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get("/admin/wiki/articles");
      const arr = r.data?.articles || [];
      setArticles(arr);
      setCanEdit(!!r.data?.can_edit);
      setCanMaster(!!r.data?.can_master);
      setTier(Number(r.data?.tier) || 1);
      if (arr.length > 0) {
        // Prefer the URL-supplied slug when we deep-linked in.
        setActiveSlug((prev) => prev || routeSlug || arr[0].slug);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Wiki load failed");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  // v2.7.65+E121 · Article Change Ping — when any L5 admin edits or deletes
  // an article on another tab / session, the WS `wiki.article_changed`
  // frame fires this event → we reload the tree + active article so
  // stale content never gets read.
  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const onWikiChange = (e) => {
      const slug = e?.detail?.slug;
      reload();
      // Force-refresh the currently open article if that's the one
      // that just changed elsewhere.
      if (slug && slug === activeSlug) {
        adminApi.get(`/admin/wiki/articles/${slug}`)
          .then((r) => setActiveArticle(r.data))
          .catch(() => { /* article may have been deleted */ });
      }
      const event = e?.detail?.event;
      const actor = e?.detail?.actor;
      const label = event === "article.deleted" ? "Article deleted" : "Article updated";
      const desc = [e?.detail?.title, actor && `by ${actor}`].filter(Boolean).join(" · ");
      toast(label, { description: desc, duration: 4000 });
    };
    window.addEventListener("rtg:wiki_article_changed", onWikiChange);
    return () => window.removeEventListener("rtg:wiki_article_changed", onWikiChange);
  }, [reload, activeSlug]);

  useEffect(() => {
    if (!activeSlug) { setActiveArticle(null); return; }
    let cancelled = false;
    adminApi.get(`/admin/wiki/articles/${activeSlug}`)
      .then((r) => { if (!cancelled) setActiveArticle(r.data); })
      .catch(() => { if (!cancelled) setActiveArticle(null); });
    return () => { cancelled = true; };
  }, [activeSlug]);

  // Debounced full-text search — hits /admin/wiki/search when the
  // query is ≥2 chars. Clears results the moment the field is emptied.
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setSearchResults(null);
      setSearching(false);
      return undefined;
    }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const r = await adminApi.get("/admin/wiki/search", { params: { q: term } });
        setSearchResults(r.data?.results || []);
      } catch (e) {
        setSearchResults([]);
      } finally { setSearching(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  // Keyboard shortcuts — `/` focuses the search box, Escape clears.
  useEffect(() => {
    const onKey = (e) => {
      const el = document.activeElement;
      const inField = el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA");
      if (e.key === "/" && !inField) {
        e.preventDefault();
        document.querySelector('[data-testid="wiki-search-input"]')?.focus();
      } else if (e.key === "Escape" && inField && el?.dataset?.testid === "wiki-search-input") {
        setQ("");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const grouped = useMemo(() => {
    const map = new Map();
    for (const a of articles) {
      const cat = a.category || "General";
      if (!map.has(cat)) map.set(cat, []);
      map.get(cat).push(a);
    }
    return Array.from(map.entries());
  }, [articles]);

  const compileManual = async (kind) => {
    setCompiling(kind);
    try {
      const url = kind === "master"
        ? "/admin/wiki/manual/master"
        : "/admin/wiki/manual/user";
      const r = await adminApi.get(url, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/pdf" });
      const objectUrl = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      const stamp = new Date().toISOString().slice(0, 10);
      link.download = kind === "master"
        ? `buller-taxi-master-manual-${stamp}.pdf`
        : `buller-taxi-user-manual-${stamp}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(objectUrl);
      toast.success(`${kind === "master" ? "Master" : "User"} manual downloaded`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Compile failed");
    } finally { setCompiling(null); }
  };

  return (
    <div className="min-h-[calc(100vh-60px)] bg-slate-50 dark:bg-zinc-950" data-testid="knowledge-center-page">
      {/* Toolbar */}
      <div className="sticky top-0 z-10 border-b border-slate-200 dark:border-zinc-800 bg-white/90 dark:bg-zinc-950/90 backdrop-blur px-5 py-3">
        <div className="max-w-7xl mx-auto flex items-center gap-3 flex-wrap">
          <BookOpen className="h-5 w-5 text-amber-500 flex-shrink-0"/>
          <div className="flex-1 min-w-0">
            <div className="text-lg font-black tracking-tight text-slate-900 dark:text-zinc-100">
              Knowledge Center
            </div>
            <div className="text-[11px] uppercase tracking-widest text-slate-500 dark:text-zinc-500">
              Buller Taxi · Operations & Developer Wiki · Tier {tier}
            </div>
          </div>
          <Button
            onClick={() => compileManual("user")}
            disabled={compiling !== null || loading || articles.length === 0}
            data-testid="wiki-compile-user-manual"
            className="bg-amber-500 hover:bg-amber-400 text-zinc-950 font-bold gap-1.5"
            size="sm"
          >
            {compiling === "user"
              ? <Loader2 className="h-4 w-4 animate-spin"/>
              : <Download className="h-4 w-4"/>}
            Compile User Manual
          </Button>
          {canMaster && (
            <Button
              onClick={() => compileManual("master")}
              disabled={compiling !== null || loading}
              data-testid="wiki-compile-master-manual"
              variant="outline"
              size="sm"
              className="gap-1.5 border-rose-400 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/20"
              title="Head Developer only · includes DB schema + API routes + env keys"
            >
              {compiling === "master"
                ? <Loader2 className="h-4 w-4 animate-spin"/>
                : <ShieldCheck className="h-4 w-4"/>}
              Master Manual (Dev)
            </Button>
          )}
          <Button
            onClick={reload}
            disabled={loading}
            data-testid="wiki-refresh"
            variant="ghost"
            size="sm"
          >
            {loading
              ? <Loader2 className="h-4 w-4 animate-spin"/>
              : <RefreshCw className="h-4 w-4"/>}
          </Button>
        </div>
      </div>

      {/* Body */}
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-[280px_1fr] gap-4 p-5">
        {/* Left rail */}
        <aside className="rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 overflow-hidden">
          <div className="p-3 border-b border-slate-200 dark:border-zinc-800">
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-slate-400"/>
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search articles"
                data-testid="wiki-search-input"
                className="h-8 text-sm pl-8 pr-14"
              />
              <span className="absolute right-2 top-2 text-[10px] text-slate-400 dark:text-zinc-500 pointer-events-none select-none">
                {searching ? (
                  <Loader2 className="h-3 w-3 animate-spin"/>
                ) : q ? (
                  <button
                    type="button"
                    onClick={() => setQ("")}
                    data-testid="wiki-search-clear"
                    className="pointer-events-auto rounded px-1 hover:bg-slate-100 dark:hover:bg-zinc-800"
                    title="Clear (Esc)"
                  >
                    ×
                  </button>
                ) : (
                  <kbd className="border border-slate-300 dark:border-zinc-700 rounded px-1 font-mono text-[9px]">/</kbd>
                )}
              </span>
            </div>
            {searchResults !== null && (
              <div
                className="mt-2 text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500"
                data-testid="wiki-search-count"
              >
                {searchResults.length === 0
                  ? `No hits for "${q}"`
                  : `${searchResults.length} match${searchResults.length === 1 ? "" : "es"} for "${q}"`}
              </div>
            )}
          </div>
          <div className="max-h-[70vh] overflow-y-auto py-1">
            {loading && (
              <div className="p-4 flex items-center gap-2 text-sm text-slate-500 dark:text-zinc-500">
                <Loader2 className="h-4 w-4 animate-spin"/>Loading wiki…
              </div>
            )}
            {!loading && articles.length === 0 && (
              <div className="p-4 text-sm italic text-slate-500 dark:text-zinc-500">
                No articles available at your tier.
              </div>
            )}

            {/* Search-results mode: flat list with highlighted snippets */}
            {searchResults !== null && (
              searchResults.length === 0 ? (
                <div className="p-4 text-sm italic text-slate-500 dark:text-zinc-500" data-testid="wiki-search-empty">
                  Try a different keyword — search covers titles, categories, and full article text.
                </div>
              ) : (
                <div data-testid="wiki-search-results">
                  {searchResults.map((r) => (
                    <button
                      key={r.slug}
                      type="button"
                      onClick={() => setActiveSlug(r.slug)}
                      data-testid={`wiki-search-hit-${r.slug}`}
                      className={`w-full text-left px-3 py-2 text-sm flex flex-col gap-1 hover:bg-slate-100 dark:hover:bg-zinc-800 border-b border-slate-100 dark:border-zinc-800/50 ${
                        activeSlug === r.slug
                          ? "bg-amber-50 dark:bg-amber-900/20 border-l-2 border-l-amber-500"
                          : "border-l-2 border-l-transparent"
                      }`}
                    >
                      <div className="flex items-center justify-between gap-2 min-w-0">
                        <span className="flex items-center gap-2 min-w-0">
                          <FileText className="h-3.5 w-3.5 text-slate-400 flex-shrink-0"/>
                          <span className="truncate font-bold">{r.title}</span>
                        </span>
                        {r.audience === "l5_only" && (
                          <span className="text-[9px] uppercase tracking-widest font-bold px-1 rounded bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                            L5
                          </span>
                        )}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-slate-500 dark:text-zinc-500">
                        {r.category}
                      </div>
                      {r.snippet && (
                        <div
                          className="text-[11px] leading-snug text-slate-600 dark:text-zinc-400 [&_mark]:bg-amber-200 [&_mark]:dark:bg-amber-500/40 [&_mark]:text-slate-900 [&_mark]:dark:text-amber-50 [&_mark]:rounded-sm [&_mark]:px-0.5"
                          dangerouslySetInnerHTML={{ __html: r.snippet }}
                        />
                      )}
                    </button>
                  ))}
                </div>
              )
            )}

            {/* Browse mode: grouped categorised list (only when no search is active) */}
            {searchResults === null && grouped.map(([cat, list]) => (
              <div key={cat} className="mb-3" data-testid={`wiki-group-${cat.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="px-3 pt-2 text-[10px] uppercase tracking-widest font-bold text-slate-500 dark:text-zinc-500">
                  {cat}
                </div>
                {list.map((a) => (
                  <button
                    key={a.slug}
                    type="button"
                    onClick={() => setActiveSlug(a.slug)}
                    data-testid={`wiki-article-tile-${a.slug}`}
                    className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-slate-100 dark:hover:bg-zinc-800 ${
                      activeSlug === a.slug
                        ? "bg-amber-50 dark:bg-amber-900/20 border-l-2 border-amber-500 font-bold"
                        : "border-l-2 border-transparent"
                    }`}
                  >
                    <span className="flex items-center gap-2 min-w-0">
                      <FileText className="h-3.5 w-3.5 text-slate-400 flex-shrink-0"/>
                      <span className="truncate">{a.title}</span>
                    </span>
                    {a.audience === "l5_only" && (
                      <span
                        className="text-[9px] uppercase tracking-widest font-bold px-1 rounded bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300"
                        title="Level 5 only"
                      >
                        L5
                      </span>
                    )}
                    <ChevronRight className="h-3 w-3 text-slate-400 flex-shrink-0"/>
                  </button>
                ))}
              </div>
            ))}
          </div>
        </aside>

        {/* Right pane */}
        <main
          className="rounded-lg border border-slate-200 dark:border-zinc-800 bg-white dark:bg-zinc-900 p-5"
          data-testid="wiki-article-body"
        >
          {activeArticle ? (
            <>
              <div className="mb-4">
                <div className="text-[10px] uppercase tracking-widest font-bold text-amber-600 dark:text-amber-400 mb-1">
                  {activeArticle.category || "General"}
                </div>
                <h1 className="text-2xl font-black tracking-tight text-slate-900 dark:text-zinc-100"
                    data-testid="wiki-article-title">
                  {activeArticle.title}
                </h1>
                <div className="text-[11px] text-slate-500 dark:text-zinc-500 mt-1">
                  Last updated · {activeArticle.updated_at
                    ? new Date(activeArticle.updated_at).toLocaleDateString()
                    : "—"}
                  {activeArticle.audience === "l5_only" && (
                    <span className="ml-2 uppercase tracking-widest font-bold text-rose-500 text-[9px]">
                      L5 CONFIDENTIAL
                    </span>
                  )}
                </div>
              </div>
              {/* v2.7.65+E133 · SOP articles that ship with an attached PDF
                  reference expose a `pdf_asset_url` field. Show a prominent
                  download CTA at the top of the article body. */}
              {activeArticle.pdf_asset_url && (
                <a
                  href={activeArticle.pdf_asset_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white font-bold px-4 py-2.5 mb-4 transition"
                  data-testid={`wiki-article-pdf-${activeArticle.slug}`}
                >
                  <FileText className="h-5 w-5"/>
                  Download Official SOP PDF
                  <Download className="h-4 w-4"/>
                </a>
              )}
              <MarkdownBlock md={activeArticle.content_md}/>
              {canEdit && !activeArticle.seeded && (
                <div className="mt-6 pt-4 border-t border-slate-200 dark:border-zinc-800 text-[11px] text-slate-500 dark:text-zinc-500">
                  Editable · use the API (PATCH /admin/wiki/articles/{activeArticle.slug}) to update this page.
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-16 text-slate-500 dark:text-zinc-500">
              <BookOpen className="h-8 w-8 mx-auto mb-2 opacity-40"/>
              Select an article from the list to begin.
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
